"use client";

import React, { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import ReviewCard from './ReviewCard';
import StarRating from './StarRating';
import WriteReviewForm from './WriteReviewForm';
import { PenTool, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { getOrCreateGuestId, getGuestAvatar, getGuestName } from '../stories/utils/videoUtils';
import { Skeleton } from '@/components/ui/skeleton';

interface ReviewsTabContentProps {
  brandId: string;
  productId?: string | null; // null = show all brand reviews
}

const ReviewsTabContent: React.FC<ReviewsTabContentProps> = ({ brandId, productId }) => {
  const [isWriting, setIsWriting] = useState(false);

  // For now, show all brand reviews (we can add product filtering later)
  // Query reviews
  const reviews = useQuery(
    api.reviews.queries.getBrandReviews,
    brandId ? { brandId: brandId as any, status: "approved" } : "skip"
  );

  // Query rating stats
  const stats = useQuery(
    api.reviews.queries.getBrandReviewStats,
    brandId ? { brandId: brandId as any } : "skip"
  );

  // Mutations
  const createReview = useMutation(api.reviews.mutations.createReview);
  const voteHelpful = useMutation(api.reviews.mutations.voteReviewHelpful);

  const handleSubmitReview = async (reviewData: {
    rating: number;
    title: string;
    content: string;
    authorName?: string;
  }) => {
    // Throw, never return early: WriteReviewForm shows its own success toast
    // when this resolves, so a silent return told the customer "submitted"
    // while nothing was saved (Korean Beauty, Sep 2026).
    if (!brandId) {
      throw new Error("Brand not available");
    }

    try {
      // Get or create guest ID
      const guestId = getOrCreateGuestId();
      const guestName = getGuestName(guestId);
      const guestAvatar = getGuestAvatar(guestId);

      // From the brand page the review is about the store itself (no
      // product); a product page passes its productId.
      await createReview({
        brandId: brandId as any,
        productId: productId ? (productId as Id<"products">) : undefined,
        rating: reviewData.rating,
        title: reviewData.title,
        content: reviewData.content,
        guestId,
        userName: reviewData.authorName?.trim() || guestName,
        userAvatar: guestAvatar,
      });

      setIsWriting(false);
    } catch (error) {
      console.error('Failed to create review:', error);
      throw error;
    }
  };

  const handleVote = async (reviewId: Id<"productReviews">) => {
    try {
      await voteHelpful({ reviewId });
      toast.success('Marked as helpful!');
    } catch (error) {
      console.error('Failed to vote:', error);
      toast.error('Failed to vote');
    }
  };

  // Loading state
  if (reviews === undefined || stats === undefined) {
    return (
      <div className="max-w-3xl mx-auto">
        {/* Header Summary Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-8 flex flex-col md:flex-row gap-8 items-center">
          {/* Left: Big Number Skeleton */}
          <div className="text-center shrink-0">
            <Skeleton className="h-16 w-24 mx-auto mb-2" />
            <Skeleton className="h-5 w-32 mx-auto mb-2" />
            <Skeleton className="h-4 w-24 mx-auto" />
          </div>

          {/* Middle: Bars Skeleton */}
          <div className="flex-1 w-full space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-4 w-3" />
                <Skeleton className="h-2 flex-1" />
                <Skeleton className="h-4 w-8" />
              </div>
            ))}
          </div>

          {/* Right: Button Skeleton */}
          <div className="shrink-0">
            <Skeleton className="h-12 w-40" />
          </div>
        </div>

        {/* Filter Bar Skeleton */}
        <div className="flex justify-between items-center mb-6">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-9 w-32" />
        </div>

        {/* Review List Skeleton */}
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-start gap-4 mb-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-40" />
                </div>
              </div>
              <Skeleton className="h-6 w-3/4 mb-2" />
              <Skeleton className="h-4 w-full mb-1" />
              <Skeleton className="h-4 w-full mb-1" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const approvedReviews = reviews || [];
  const avgRating = stats?.averageRating || 0;
  const totalReviews = stats?.totalReviews || 0;

  // Calculate distribution
  const distribution = [5, 4, 3, 2, 1].map(star => {
    const count = approvedReviews.filter((r: any) => Math.round(r.rating) === star).length;
    const percentage = approvedReviews.length ? (count / approvedReviews.length) * 100 : 0;
    return { star, count, percentage };
  });

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header Summary */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-8 flex flex-col md:flex-row gap-8 items-center">

        {/* Left: Big Number */}
        <div className="text-center shrink-0">
          <div className="text-5xl font-extrabold text-slate-900 mb-2">{avgRating.toFixed(1)}</div>
          <StarRating rating={avgRating} size={20} readonly />
          <div className="text-sm text-slate-500 mt-2">{totalReviews} Reviews</div>
        </div>

        {/* Middle: Bars */}
        <div className="flex-1 w-full space-y-2">
          {distribution.map(d => (
            <div key={d.star} className="flex items-center gap-3 text-sm">
              <span className="font-bold w-3">{d.star}</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-400 rounded-full"
                  style={{ width: `${d.percentage}%` }}
                />
              </div>
              <span className="text-slate-400 w-8 text-right">{d.count}</span>
            </div>
          ))}
        </div>

        {/* Right: Write Button */}
        <div className="shrink-0">
          <button
            onClick={() => setIsWriting(true)}
            className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-200 flex items-center gap-2"
          >
            <PenTool className="w-4 h-4" />
            Write a Review
          </button>
        </div>
      </div>

      {/* Write Review Form */}
      {isWriting && (
        <WriteReviewForm
          onSubmit={handleSubmitReview}
          onCancel={() => setIsWriting(false)}
        />
      )}

      {/* Filter Bar (Visual only for now) */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-bold text-slate-900 text-lg">Customer Reviews</h3>
        <button className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:bg-slate-100 px-3 py-1.5 rounded-lg transition-colors">
          <Filter className="w-4 h-4" />
          Most Relevant
        </button>
      </div>

      {/* Review List */}
      <div className="space-y-6">
        {approvedReviews.map((review: any) => (
          <ReviewCard
            key={review._id}
            review={review}
            currentUserId="guest"
            onVote={handleVote}
          />
        ))}
        {approvedReviews.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No reviews yet. Be the first to review!
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsTabContent;
